import { RoutePaths } from "enums/RoutePaths";
import { IconName } from "stories/Admin";

export type AdminRole = "moske" | "super-moske" | "superadmin" | "mawaqeet";

/** Which inquiry counter a menu item or tab shows (from GET /api/v1/superadmin/inquiries/summary). */
export type CountKey =
  | "total"
  | "contactUnread"
  | "adminRequestsPending"
  | "mosqueSuggestionsNew"
  | "janazahPending";

export interface MenuItemConfig {
  label: string;
  icon: IconName;
  path: RoutePaths;
  /**
   * Pages shown as tabs under this menu item. The item is active on any of
   * them, and the layout shows the tabs above the page.
   */
  tabs?: Array<{ label: string; path: RoutePaths; count?: CountKey }>;
  /** Shows a counter after the label when it is above zero. */
  count?: CountKey;
}

export interface MenuGroup {
  title: string;
  items: MenuItemConfig[];
}

const mosqueGroups: MenuGroup[] = [
  {
    title: "MOSKEEN",
    items: [
      { label: "Moské-info", icon: "mosque", path: RoutePaths.Root },
      {
        label: "Min moské-oppsett",
        icon: "table",
        path: RoutePaths.MyMosqueLayout,
      },
      { label: "API", icon: "code", path: RoutePaths.Api },
      { label: "Administratorer", icon: "users", path: RoutePaths.Admins },
    ],
  },
  {
    title: "TIDER",
    items: [
      { label: "Jamat-tider", icon: "people", path: RoutePaths.Jamat },
      { label: "Bønnetider", icon: "table", path: RoutePaths.PrayerTimes },
      {
        label: "PDF-generator",
        icon: "download",
        path: RoutePaths.PdfGenerator,
      },
      {
        label: "Jummah, Taraweeh og Eid",
        icon: "clock",
        path: RoutePaths.SpecialPrayerSettings,
      },
    ],
  },
  {
    title: "KOMMUNIKASJON",
    items: [
      { label: "Push-meldinger", icon: "bell", path: RoutePaths.Notification },
      { label: "Informasjon", icon: "info", path: RoutePaths.Information },
      // Plakater vises i appen og på TV, så de har en egen side utenfor «TV».
      { label: "Plakater", icon: "upload", path: RoutePaths.PostersAll },
      {
        label: "TV",
        icon: "tv",
        path: RoutePaths.Posters,
        tabs: [
          { label: "Plakater på TV", path: RoutePaths.Posters },
          { label: "Lag plakat", path: RoutePaths.TvStudio },
          { label: "Visningsplan", path: RoutePaths.TvSchedule },
          { label: "TV-skjermer", path: RoutePaths.Tv },
        ],
      },
    ],
  },
  {
    title: "INNHOLD",
    items: [
      { label: "Aktiviteter", icon: "cal", path: RoutePaths.Event },
      { label: "Blogginnlegg", icon: "doc", path: RoutePaths.Blog },
      { label: "Kampanjer", icon: "heart", path: RoutePaths.Campaign },
      { label: "Innsamlet", icon: "chart", path: RoutePaths.Donations },
      { label: "Betaling", icon: "key", path: RoutePaths.PaymentSettings },
    ],
  },
];

const superAdminGroups: MenuGroup[] = [
  {
    title: "OVERSIKT",
    items: [
      {
        label: "Oversikt",
        icon: "chart",
        path: RoutePaths.Overview,
        tabs: [
          { label: "Oversikt", path: RoutePaths.Overview },
          { label: "Bruk av appen", path: RoutePaths.AppUsage },
        ],
      },
      { label: "Innsamlet", icon: "heart", path: RoutePaths.AdminDonations },
      {
        label: "Henvendelser",
        icon: "inbox",
        path: RoutePaths.Inquiries,
        count: "total",
        tabs: [
          { label: "Oversikt", path: RoutePaths.Inquiries },
          {
            label: "Innboks",
            path: RoutePaths.ContactInbox,
            count: "contactUnread",
          },
          {
            label: "Admin-søknader",
            path: RoutePaths.AdminRequests,
            count: "adminRequestsPending",
          },
          {
            label: "Nye moskeer",
            path: RoutePaths.MosqueSuggestions,
            count: "mosqueSuggestionsNew",
          },
        ],
      },
      { label: "Moskeer", icon: "mosque", path: RoutePaths.MosqueAdmin },
      { label: "Brukere og tilganger", icon: "users", path: RoutePaths.Users },
      {
        label: "Kursregistreringer",
        icon: "inbox",
        path: RoutePaths.Registreringer,
      },
      // Not in the design, kept so the page stays reachable.
      {
        label: "Moské-statistikk",
        icon: "chart",
        path: RoutePaths.MosqueStats,
      },
    ],
  },
  {
    title: "BØNNETIDER",
    items: [
      {
        label: "Bønnetider",
        icon: "table",
        path: RoutePaths.SuperAdminPrayerTimes,
        tabs: [
          { label: "Bønnetider", path: RoutePaths.SuperAdminPrayerTimes },
          { label: "Generer tider", path: RoutePaths.SuperAdminGenerateTimes },
        ],
      },
      {
        label: "Lokasjoner og tabeller",
        icon: "pin",
        path: RoutePaths.Locations,
      },
      { label: "Hijri-datoer", icon: "hijri", path: RoutePaths.HijriDates },
      {
        label: "Kalender-logg",
        icon: "doc",
        path: RoutePaths.PrayerCalendarLog,
      },
      { label: "Eksterne kilder", icon: "link", path: RoutePaths.External },
    ],
  },
  {
    title: "KOMMUNIKASJON",
    items: [
      {
        label: "Push-meldinger",
        icon: "bell",
        path: RoutePaths.GlobalPush,
        tabs: [
          { label: "Alle brukere", path: RoutePaths.GlobalPush },
          { label: "Alle admins", path: RoutePaths.AdminPush },
          { label: "Én bruker", path: RoutePaths.DirectPush },
          { label: "Push-logg", path: RoutePaths.PushLog },
        ],
      },
    ],
  },
  {
    title: "INNHOLD",
    items: [
      {
        label: "Duaer, navn og uttrykk",
        icon: "book",
        path: RoutePaths.Dua,
        tabs: [
          { label: "Duaer", path: RoutePaths.Dua },
          { label: "Allahs navn", path: RoutePaths.NamesOfAllah },
          { label: "Uttrykk", path: RoutePaths.Expressions },
        ],
      },
      { label: "Koranen", icon: "quote", path: RoutePaths.Quran },
      // Læringsstien for barn i appene (iBønn Kids).
      { label: "iBønn Kids", icon: "people", path: RoutePaths.Kids },
      { label: "Dagens hadith", icon: "star", path: RoutePaths.Hadith },
      // Not in the design, kept so the page stays reachable.
      {
        label: "Janazah",
        icon: "moon",
        path: RoutePaths.Janazah,
        count: "janazahPending",
        tabs: [
          {
            label: "Dødsannonser",
            path: RoutePaths.Janazah,
            count: "janazahPending",
          },
          { label: "AI-logg (janazah)", path: RoutePaths.JanazahAiLog },
          { label: "Begravelsesbyråer", path: RoutePaths.FuneralAgencies },
        ],
      },
    ],
  },
  {
    title: "AIBØNN",
    items: [
      {
        label: "Test og AI-imam",
        icon: "spark",
        path: RoutePaths.AiImam,
        tabs: [
          { label: "AI-imam", path: RoutePaths.AiImam },
          { label: "Test av prompter", path: RoutePaths.AiBonnPrompter },
        ],
      },
      {
        label: "Tilbakemeldinger",
        icon: "thumbs",
        path: RoutePaths.AiBonFeedback,
      },
    ],
  },
  {
    title: "SYSTEM",
    items: [
      { label: "Cache", icon: "db", path: RoutePaths.Cache },
      // Not in the design, kept so the page stays reachable.
      { label: "API-nøkler", icon: "key", path: RoutePaths.ApiKeys },
      { label: "Partnere", icon: "link", path: RoutePaths.Partners },
      {
        label: "TV-skjermer",
        icon: "tv",
        path: RoutePaths.TvDevices,
        tabs: [
          { label: "TV-er", path: RoutePaths.TvDevices },
          { label: "AI-logg", path: RoutePaths.TvAiLog },
        ],
      },
    ],
  },
];

const mawaqeetGroups: MenuGroup[] = [
  {
    title: "MAWAQEET",
    items: [
      {
        label: "Bønnetider",
        icon: "table",
        path: RoutePaths.MawaqeetPrayerTimes,
      },
      { label: "Push-meldinger", icon: "bell", path: RoutePaths.MawaqeetPush },
      { label: "Lokasjoner", icon: "pin", path: RoutePaths.MawaqeetLocations },
      {
        label: "Generer tider",
        icon: "wand",
        path: RoutePaths.MawaqeetGenerateTimes,
      },
      {
        label: "Ramadan-kalender",
        icon: "moon",
        path: RoutePaths.MawaqeetRamadan,
      },
    ],
  },
];

export const menuGroupsFor = (role: AdminRole): MenuGroup[] =>
  role === "superadmin"
    ? [...superAdminGroups, ...mawaqeetGroups]
    : role === "mawaqeet"
      ? mawaqeetGroups
      : mosqueGroups;

export const isItemActive = (item: MenuItemConfig, pathname: string) =>
  item.path === pathname || !!item.tabs?.some((t) => t.path === pathname);

export const roleBadge: Record<AdminRole, { label: string; tone: string }> = {
  moske: { label: "MOSKÉ", tone: "#8FE0B6" },
  "super-moske": { label: "SUPER", tone: "#E2C58A" },
  superadmin: { label: "SUPER", tone: "#E2C58A" },
  mawaqeet: { label: "MAWAQEET", tone: "#E2C58A" },
};

/** «971234567» → «971 234 567». Other values are returned unchanged. */
export const formatOrgNumber = (orgNr: string | null | undefined) =>
  orgNr && /^\d{9}$/.test(orgNr)
    ? `${orgNr.slice(0, 3)} ${orgNr.slice(3, 6)} ${orgNr.slice(6)}`
    : (orgNr ?? "");
